import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { APP_TIMEZONE } from "./format";

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

/** Yagona ko'rinish: kun.oy.yil, vaqt ixtiyoriy. */
export const DATE_FORMAT = "DD.MM.YYYY";
export const DATE_TIME_FORMAT = "DD.MM.YYYY HH:mm";

const DATE_DIGITS = 8;
const DATE_TIME_DIGITS = 12;

export interface DateRange {
  /** ISO 8601, UTC — backend vaqt belgilarini shu ko'rinishda kutadi. */
  from: string;
  to: string;
}

/** Kiritilgan qatordagi raqamlar soni — to'liqlik shu bo'yicha o'lchanadi. */
const digitsOf = (value: string): string => value.replace(/\D/g, "");

/**
 * Yozilayotgan matnni `DD.MM.YYYY hh:mm` qolipiga soladi.
 *
 * Ajratuvchilarni odam yozmaydi — ular O'ZI qo'yiladi: `03092026` ham,
 * `03.09.2026` ham bir xil natija beradi. Sana tanlagichdan voz kechish
 * sharti ham shu: klaviaturadan yozish tanlagichdan tez bo'lishi kerak.
 */
export function maskDateInput(raw: string, withTime = false): string {
  const digits = digitsOf(raw).slice(0, withTime ? DATE_TIME_DIGITS : DATE_DIGITS);

  const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(Boolean);
  let result = parts.join(".");

  if (digits.length > 8) result += ` ${digits.slice(8, 10)}`;
  if (digits.length > 10) result += `:${digits.slice(10, 12)}`;

  return result;
}

/** Qolip to'lganmi: qidiruv aynan shu paytda o'zi ishga tushadi. */
export function isDateInputComplete(value: string, withTime = false): boolean {
  return digitsOf(value).length === (withTime ? DATE_TIME_DIGITS : DATE_DIGITS);
}

/**
 * Kiritilgan qatorni sanaga o'giradi.
 *
 * Qat'iy qolip (`strict`): `32.13.2026` — sana emas, va uni dayjs
 * jimgina keyingi oyga surib yuborishi kerak emas. Vaqt yozilmagan
 * bo'lsa kun boshi qaytadi.
 */
export function parseDateInput(value: string): dayjs.Dayjs | null {
  const digits = digitsOf(value);

  if (digits.length === DATE_DIGITS) {
    const parsed = dayjs(value, DATE_FORMAT, true);
    return parsed.isValid() ? parsed : null;
  }

  if (digits.length === DATE_TIME_DIGITS) {
    const parsed = dayjs(value, DATE_TIME_FORMAT, true);
    return parsed.isValid() ? parsed : null;
  }

  return null;
}

/**
 * Kiritilgan sanadan qidiruv oralig'i.
 *
 * Vaqt belgilari bazada soniya (va undan mayda) aniqlikda saqlanadi,
 * ya'ni "03.09.2026" bilan TENGLIK hech qachon topilmasdi. Shuning
 * uchun qidiruv har doim ORALIQ: kun yozilsa — o'sha kunning boshidan
 * oxirigacha, vaqt ham yozilsa — o'sha daqiqa.
 *
 * Oraliq TOSHKENT kunida hisoblanadi va UTC da yuboriladi: admin
 * Toshkent kunini nazarda tutadi, baza esa UTC da yashaydi. Brauzer
 * zonasida hisoblansa, chet eldagi moderator "03.09.2026" deb yozib
 * boshqa oraliqni qidirardi.
 */
export function dateInputRange(value: string): DateRange | undefined {
  const parsed = parseDateInput(value);
  if (!parsed) return undefined;

  const unit = digitsOf(value).length === DATE_TIME_DIGITS ? "minute" : "day";

  return {
    from: parsed.tz(APP_TIMEZONE, true).startOf(unit).toISOString(),
    to: parsed.tz(APP_TIMEZONE, true).endOf(unit).toISOString(),
  };
}
